import { Suspense, lazy, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { HttpAgent } from '@ag-ui/client';
import { MyAuth0Context } from 'index';
import { AGENT_RUNTIME_URL, VANTAGE_AGENT_ID } from 'shared/constants';
import CatchAllToolRenderRegistry from '../actions/registerCatchAllToolRender';
import { ToolCallRegistryAvailable } from '../actions/registryAvailability';
import { ChatRuntimeContext } from './context';
import { useCopilotKitRuntime } from './copilotKitRuntime';

// Lazy-load the CopilotKit v2 provider so the SDK (and its CSS side-effect import)
// is only resolved at runtime, not in the static import graph of `index.tsx`.
// `index.tsx` is transitively loaded by anything that touches `MyAuth0Context`
// (useAxios, useAuth, useWorkato, ...), and pulling CopilotKit eagerly into that
// chain would break vitest's CSS handling for unrelated test files.
const CopilotKitProviderLazy = lazy(async () => {
  const mod = await import('@copilotkit/react-core/v2');
  return { default: mod.CopilotKitProvider };
});

const REFRESH_INTERVAL_MS = 4 * 60 * 1000;

/**
 * Mounts the CopilotKit v2 provider with an Auth0-derived bearer token, registers
 * the Vantage `HttpAgent` as a self-managed agent (so `useAgent(VANTAGE_AGENT_ID)`
 * resolves without a runtime `/info` handshake), and exposes `useCopilotKitRuntime`
 * to the chat drawer's subtree via `ChatRuntimeContext`.
 *
 * Token rotation uses the function-form `headers` callback documented at
 * `node_modules/@copilotkit/react-core/dist/copilotkit-DFaI4j2r.d.mts:2137`. The
 * callback closes over a ref refreshed every {@link REFRESH_INTERVAL_MS}; bumping
 * `tokenVersion` re-renders the provider so the freshest token flows through
 * `core.setHeaders()` and propagates to live agents without remounting.
 *
 * Children always render so unauthenticated routes (login redirect) and the older
 * ChatBot system are never blocked. The CopilotKitProvider itself is held back
 * until a non-empty token is available, but the outer Suspense is mounted from
 * the start so React never sees the outer element type change — a Fragment ↔
 * Suspense swap unmounts the entire app subtree, which previously surfaced as
 * a `"current" is read-only` throw from React's ref-detach path on cold load.
 *
 * Lives inside `client-chat/runtime/` so the no-restricted-imports lint rule can
 * keep `@copilotkit/*` and `@ag-ui/*` confined to the runtime + actions directories.
 * @param props - Component props
 * @param props.children - Child components to render inside (or alongside) the provider
 * @returns Children always render; CopilotKitProvider wraps them once a token exists.
 */
export const CopilotKitMount = ({ children }: { children: React.ReactNode }) => {
  const { getAccessTokenSilently, isAuthenticated } = useContext(MyAuth0Context);
  const tokenRef = useRef<string>('');
  const [tokenVersion, setTokenVersion] = useState(0);
  const [hasToken, setHasToken] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      tokenRef.current = '';
      setHasToken(false);
      return;
    }
    let cancelled = false;
    const apply = (token: string) => {
      if (cancelled) return;
      const trimmed = token || '';
      const previous = tokenRef.current;
      tokenRef.current = trimmed;
      if (!previous && trimmed) setHasToken(true);
      if (previous !== trimmed) setTokenVersion((v) => v + 1);
    };

    getAccessTokenSilently()
      .then(apply)
      .catch(() => apply(''));

    const interval = window.setInterval(() => {
      getAccessTokenSilently()
        .then(apply)
        .catch(() => {
          /* swallow — Auth0 will surface auth errors elsewhere */
        });
    }, REFRESH_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [isAuthenticated, getAccessTokenSilently]);

  // Stable HttpAgent registered via `selfManagedAgents`. Constructing it once
  // means the same instance is reused across renders, and CopilotKit's header
  // sync (applyHeadersToAgents) keeps `agent.headers.Authorization` fresh as
  // the provider re-renders with new merged headers. Cast around the dual
  // `@ag-ui/client` install (root vs. transitive via runtime-client-gql) —
  // both types describe the same runtime class.
  const selfManagedAgents = useMemo(() => {
    const httpAgent = new HttpAgent({
      url: AGENT_RUNTIME_URL,
      agentId: VANTAGE_AGENT_ID,
      description: 'Vantage Advisor agent (Phase 2 stub)'
    });
    return { [VANTAGE_AGENT_ID]: httpAgent } as never;
  }, []);

  // Function-form headers callback. The provider invokes it during the render
  // pass that produces a new `mergedHeaders` (see CopilotKitProvider.tsx:399);
  // bumping `tokenVersion` is what triggers the re-render that re-evaluates
  // the callback and propagates the new token through `core.setHeaders()`.
  const headers = useMemo<() => Record<string, string>>(
    () => () => {
      const token = tokenRef.current;
      const result: Record<string, string> = {};
      if (token) result.Authorization = `Bearer ${token}`;
      return result;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tokenVersion]
  );

  // Always render the same outer shape so React never unmounts the entire app
  // tree when `hasToken` flips. Switching the outer element type (Fragment ↔
  // Suspense) used to remount everything below us, which surfaced as a
  // `"current" is read-only` throw from `safelyDetachRef` walking refs in the
  // unmounting subtree. The fallback is intentionally `null` rather than
  // `children` so the same `children` JSX never appears in two positions.
  return (
    <Suspense fallback={null}>
      {hasToken ? (
        <CopilotKitProviderLazy runtimeUrl={AGENT_RUNTIME_URL} headers={headers} selfManagedAgents={selfManagedAgents}>
          <ToolCallRegistryAvailable.Provider value={true}>
            <CatchAllToolRenderRegistry />
            <ChatRuntimeContext.Provider value={useCopilotKitRuntime}>{children}</ChatRuntimeContext.Provider>
          </ToolCallRegistryAvailable.Provider>
        </CopilotKitProviderLazy>
      ) : (
        children
      )}
    </Suspense>
  );
};
